import {
    IDynamicLinkedServiceTypeMetadata,
    ILinkedServiceTypeMetadata
} from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';
import { Metamodel } from 'app/model/base/model';
import { Constants } from 'app/shared/shared-constants';
import { format } from 'app/shared/utils/string.util';
import { AzureBlobFSSink } from 'app/model/pipeline/copysink/azure-blob-fs.copysink';
import { AzureBlobFSCopySource } from 'app/model/pipeline/copysource/azure-blob-fs.copysource';
import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import {
    azureResourceSelectMethodOptions,
    commonFileTypeStoreGetMetadataActivityReadSettings,
    commonFileTypeStoreDeleteActivityReadSettings,
    getCommonBinaryStoreReadSettingControls,
    getCommonFileTypeStoreReadSettingControls,
    azureCloudTypeMetadata,
    authenticationReferenceMethodOptions,
    commonFileTypeStoreWriteSettingControls,
    commonBinaryStoreWriteSettingControls,
    commonDeltaStoreWriteSettingControls,
    getCommonDeltaStoreReadSettingControls,
    maxConcurrentConnectionsMetadata,
    cdcDestination
} from 'app/registries/consts/metadata-consts';
import { adfModelTypeMap } from '../consts/adf-model-type-map-const';
import { deltaMetadata } from 'app/registries/dataset/delta-metadata';
import { sasUriSetting } from './azure-storage-shared-fields';
import { storagePEWellknownErrorMessages } from 'app/shared/utils/connector-metadata-static-consts';
import { servicePrincipalCredentialTypes } from 'app/model/linkedservice/dynamics.linkedservice';
import { GlobalVariables } from 'app/services/globals';
import {
    azureDefaultAuthRefTypes,
    azureBlobFSAuthTypes,
    CredentialTypeOptions,
    ModelConstants
} from 'shell-models-lib';
import { mergeUamiCredentialOption, mergeUserAuthCredentialOption } from '../utls/authenticationUtils';
import { ISelectOption } from 'app/interfaces/ISelectOption';
import {
    avro,
    cdm,
    delimited,
    delta,
    excel,
    json,
    orc,
    parquet,
    xml,
    iceberg
} from 'app/registries/dataflow/dataflow-file-format-metadata';
import { blockSizeInMbMetadata } from './linked-service-shared-fields';
import { storageUrlForm } from 'app/shared/constants/links.const';
import { BuiltInDataSourceOptionId, DataSourceOptionKind } from '@powerquery/mashup-editor-client';

const blockSizeInMbDesc =
    format(stringResources.BlockSizeInMB_Description, stringResources.AzureBlobFS_DisplayText) +
    ' <a target="_blank" href="https://go.microsoft.com/fwlink/?linkid=2111570">' +
    stringResources.LearnMore +
    '</a>';
const storageUrl = format(storageUrlForm, 'dfs');

const urlBase: ControlMetadata = {
    type: 'textbox',
    bind: 'url',
    label: stringResources.ModelName_Url,
    required: true,
    placeholder: storageUrl,
    validators: [
        { type: 'required', errorMessage: '' },
        {
            type: 'pattern',
            pattern: `^https://[^\/\.]+(\\.dfs\\.(${Constants.storageValidEndpointSuffixes})|\\.z[0-9]+\\.dfs\\.${Constants.partitionedDnsEndpointSuffix})/?$|^[*]{10}$`,
            errorMessage: format(stringResources.Validator_ErrorMessage_Pattern, storageUrl)
        },
        {
            type: 'pattern',
            pattern: `^https://[a-z0-9-]{3,}(\\.dfs\\.(${Constants.storageValidEndpointSuffixes})|\\.z[0-9]+\\.dfs\\.${Constants.partitionedDnsEndpointSuffix})/?$|^[*]{10}$`,
            errorMessage: stringResources.Validation_StorageAccountName
        }
    ],
    isLabelVertical: true
};

const urlInput: ControlMetadata[] = [
    {
        type: 'parameterizable',
        child: urlBase
    }
];

const tenantBase: ControlMetadata = {
    type: 'tenant-textbox',
    bind: 'tenant',
    validators: [{ type: 'required', errorMessage: '' }],
    isLabelVertical: true,
    label: stringResources.ModelName_Tenant,
    required: true
};

const tenantInput: ControlMetadata[] = [
    {
        type: 'parameterizable',
        child: tenantBase
    }
];

const servicePrincipalIdBase: ControlMetadata = {
    type: 'textbox',
    bind: 'servicePrincipalId',
    label: stringResources.ModelName_ServicePrincipalID,
    required: true,
    validators: [{ type: 'required', errorMessage: '' }],
    isLabelVertical: true
};

const servicePrincipalIdInput: ControlMetadata[] = [
    {
        type: 'parameterizable',
        child: servicePrincipalIdBase
    }
];

const servicePrincipalKeyPassword: ControlMetadata = {
    type: 'password',
    label: stringResources.ModelName_ServicePrincipalKey,
    required: true,
    isLabelVertical: true,
    validators: [{ type: 'required', errorMessage: '' }]
};

const servicePrincipalCredTypeInput: ControlMetadata[] = [
    {
        type: 'radio-switch',
        bind: 'servicePrincipalCredentialType',
        hide: '{ isOldServicePrincipalResource }',
        label: stringResources.ModelName_ServicePrincipalCredentialType,
        isLabelVertical: true,
        nested: 1,
        options: [
            {
                label: stringResources.ModelName_ServicePrincipalKey,
                value: servicePrincipalCredentialTypes.ServicePrincipalKey
            },
            {
                label: stringResources.ModelName_ServicePrincipalCert,
                value: servicePrincipalCredentialTypes.ServicePrincipalCert
            }
        ],
        required: true,
        validators: [{ type: 'required', errorMessage: '' }],
        defaultValue: servicePrincipalCredentialTypes.ServicePrincipalKey,
        cases: [
            {
                caseValue: servicePrincipalCredentialTypes.ServicePrincipalKey,
                children: [
                    {
                        type: 'dynamic-akv-switcher',
                        bind: 'servicePrincipalKey',
                        label: stringResources.ModelName_ServicePrincipalKey,
                        enableAKVParameterization: true,
                        child: {
                            type: 'parameterizable',
                            attribute: 'servicePrincipalKey',
                            child: servicePrincipalKeyPassword,
                            disableAddParameter: true
                        },
                        description: format(
                            stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                            format(
                                stringResources.ModelName_LinkedServiceTemplate,
                                stringResources.AzureBlobFS_DisplayText
                            ),
                            stringResources.ModelName_ServicePrincipalKey
                        )
                    }
                ]
            },
            {
                caseValue: servicePrincipalCredentialTypes.ServicePrincipalCert,
                children: [
                    {
                        type: 'dynamic-akv-switcher',
                        bind: 'servicePrincipalCert',
                        label: stringResources.ModelName_ServicePrincipalCert,
                        enableAKVParameterization: true,
                        validators: [{ type: 'required', errorMessage: '' }],
                        akvOnly: true,
                        isCert: true,
                        akvOptionDisplayText: stringResources.AzureKeyVault_ServicePrincipalCert_DisplayText,
                        description: format(
                            stringResources.AzureKeyVaultCertName_Tooltip_Template,
                            format(
                                stringResources.ModelName_LinkedServiceTemplate,
                                stringResources.AzureBlobFS_DisplayText
                            ),
                            stringResources.ModelName_ServicePrincipalCert
                        )
                    }
                ]
            }
        ]
    }
];

const akvSwitcherInput: ControlMetadata[] = [
    {
        type: 'dynamic-akv-switcher',
        bind: 'servicePrincipalKey',
        hide: '{ !isOldServicePrincipalResource }',
        label: stringResources.ModelName_ServicePrincipalKey,
        enableAKVParameterization: true,
        child: {
            type: 'parameterizable',
            attribute: 'servicePrincipalKey',
            child: servicePrincipalKeyPassword,
            disableAddParameter: true
        },
        description: format(
            stringResources.AzureKeyVaultSecretName_Tooltip_Template,
            format(stringResources.ModelName_LinkedServiceTemplate, stringResources.Dynamics_DisplayText),
            stringResources.ModelName_ServicePrincipalKey
        )
    }
];

function azureBlobFSAccountSetting(fetchAccountKey: boolean): ControlMetadata {
    const manualCase: ControlMetadata[] = [...urlInput];
    const password: ControlMetadata = {
        type: 'password',
        label: stringResources.ModelName_StorageAccountKey,
        required: true,
        isLabelVertical: true,
        validators: [{ type: 'required', errorMessage: '' }]
    };

    if (fetchAccountKey) {
        manualCase.push({
            type: 'dynamic-akv-switcher',
            bind: 'accountKey',
            label: stringResources.ModelName_StorageAccountKey,
            enableAKVParameterization: true,
            child: {
                type: 'parameterizable',
                attribute: 'accountKey',
                child: password,
                disableAddParameter: true
            },
            description: format(
                stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                format(stringResources.ModelName_LinkedServiceTemplate, stringResources.AzureBlobFS_DisplayText),
                stringResources.ModelName_StorageAccountKey
            )
        });
    }

    return {
        type: 'radio-switch',
        nested: 1,
        label: stringResources.AccountSelectionMethodLabel,
        description: stringResources.AzureStorage_SelectMethodDescription,
        options: azureResourceSelectMethodOptions,
        isLabelVertical: true,
        defaultValue: '{url != null || parameterized ? "manual" : "azure"}',
        cases: [
            {
                caseValue: 'azure',
                children: [
                    {
                        type: 'azure-storage-account-select',
                        bind: {
                            url: 'url',
                            accountKey: 'accountKey',
                            scope: '_accountResourceID'
                        },
                        serviceType: 'dfs',
                        showPEStatus: true,
                        blobfsOnly: true,
                        fetchAccountKey,
                        validators: fetchAccountKey
                            ? {
                                accountName: [{ type: 'required', errorMessage: '' }],
                                accountKey: [{ type: 'required', errorMessage: '' }]
                            }
                            : {
                                accountName: [{ type: 'required', errorMessage: '' }]
                            }
                    }
                ]
            },
            {
                caseValue: 'manual',
                children: manualCase.concat({
                    type: 'private-endpoint-status'
                })
            }
        ]
    };
}

// provides user with inline vs credential radio button to add authentication parameters for SAMI and SP Authentication methods.
function azureBlobFSAuthReferenceSetting(credentialTypeFilter: CredentialTypeOptions): ControlMetadata {
    return {
        type: 'radio-switch',
        bind: 'authReferenceMethod',
        nested: 1,
        label: stringResources.Authentication_Reference_Method_Label,
        description: stringResources.Authentication_Reference_Method_Description,
        options: authenticationReferenceMethodOptions,
        isLabelVertical: true,
        defaultValue: azureDefaultAuthRefTypes.InlineRefTypeAuthentication,
        cases: [
            {
                caseValue: azureDefaultAuthRefTypes.InlineRefTypeAuthentication,
                children:
                    credentialTypeFilter === CredentialTypeOptions.ServicePrincipal
                        ? [
                            // service Principal
                            ...tenantInput,
                            ...servicePrincipalIdInput,
                            ...servicePrincipalCredTypeInput,
                            ...akvSwitcherInput,
                            azureCloudTypeMetadata
                        ]
                        : [
                            // system Assigned Managed Identity
                            {
                                type: 'management-identity-info',
                                resourceType: stringResources.AzureBlobFS_DisplayText,
                                link: 'https://learn.microsoft.com/azure/data-factory/connector-azure-data-lake-storage?tabs=data-factory#managed-identity'
                            }
                        ]
            },
            {
                caseValue: azureDefaultAuthRefTypes.CredentialRefTypeAuthentication,
                children: [
                    {
                        type: 'credential-select',
                        bind: 'credential',
                        filterCredentialType: credentialTypeFilter,
                        required: true,
                        supportFic: GlobalVariables.supportFic,
                        validators: [{ type: 'required', errorMessage: '' }]
                    }
                ]
            }
        ]
    };
}

// default auth options of A/c Key, SAMI and SP. Based on flag, the fourth option will be populated inside controls.
const defaultAuthenticationOptions: Array<ISelectOption<string>> = [
    {
        label: stringResources.AuthenticationType_AccountKey,
        value: azureBlobFSAuthTypes.AccountKeyAuthentication
    },
    {
        label: stringResources.SasUri,
        value: azureBlobFSAuthTypes.SasUriAuthentication
    },
    {
        label: stringResources.AuthenticationType_ServicePrincipal,
        value: azureBlobFSAuthTypes.ServicePrincipalAuthentication
    },
    {
        label: stringResources.AuthenticationType_ManagedServiceIdentity,
        value: azureBlobFSAuthTypes.MSIAuthentication
    }
];

export const azureBlobFSMetadata: IDynamicLinkedServiceTypeMetadata = async (injector) => {
    const metadata: ILinkedServiceTypeMetadata = {
        type: adfModelTypeMap.azureBlobFS.linkedServiceType,
        displayName: stringResources.AzureBlobFS_DisplayText,
        linkedServiceKind: 'file',
        supportedInTrident: true,
        supportAutoFillCopilotInput: true,
        isKnownInPipelineContext: true,
        pqoDataSourceOption: {
            kind: DataSourceOptionKind.BuiltIn,
            id: BuiltInDataSourceOptionId.AzureDataLake
        },
        icon: {
            className: 'svg-i-100x100-AzureBlob' // todo
        },
        isFirstClass: true,
        copySinkTypes: [Metamodel.modelFor(AzureBlobFSSink).name],
        copySourceTypes: [Metamodel.modelFor(AzureBlobFSCopySource).name],
        supportRootFolder: true,
        supportPE: true,
        peGroupIDs: ['dfs'],
        peWellknowErrorMessages: storagePEWellknownErrorMessages,
        resourceTypes: ['Microsoft.Storage/storageAccounts'],
        category: ['Azure'],
        supportIncrementalBinaryCopy: true,
        supportDataFlowSource: true,
        supportOPDG: GlobalVariables.enableOPDG,
        supportDataFlowSink: true,
        dataflowSource: [avro, cdm, delimited, delta, excel, json, orc, parquet, xml, iceberg],
        dataflowSink: [avro, cdm, delimited, delta, json, orc, parquet, iceberg],
        supportCopyJobSource: true,
        supportCopyJobSink: true,
        ...(GlobalVariables.copyJobGenericCdc ? {
            cdc: {
                supportCDC: true,
                supportedCDCDestinations: cdcDestination,
                supportGenericCdc: true
            }
        } : {}),
        supportDatasetV2: true,
        locationPattern: {
            fileSystem: '([^/]*)/?[^]*',
            folderPath: '[^/]*/([^]*)/[^/]*',
            fileName: '[^]*/([^/]*)'
        },
        containerLabel: stringResources.FileSystem_DisplayText,
        containerLabelPlural: stringResources.FileSystems_DisplayText,
        locationPropertiesRequireParameters: ['fileSystem', 'folderPath', 'fileName'],
        mergeReadSettingsInTrident: true,
        pathRegex: {
            pattern: Constants.blobContainerNameValidation,
            message: stringResources.CopyWizard_Dataset_ADLSGen2_Sink_ContainerValidationError
        },
        linkedServiceNamePrefix: 'AzureDataLakeStorage',
        supportTestConnectionToFilePath: true,
        supportGetMetadataActivity: true,
        getMetadataActivityReadProperties: commonFileTypeStoreGetMetadataActivityReadSettings,
        deleteActivityReadProperties: commonFileTypeStoreDeleteActivityReadSettings,
        storeReadSettingControls: getCommonFileTypeStoreReadSettingControls(),
        storeWriteSettingControls: [
            ...commonFileTypeStoreWriteSettingControls,
            blockSizeInMbMetadata(blockSizeInMbDesc),
            {
                type: 'store-setting-metadata',
                bind: 'metadata',
                parameterizable: true
            }
        ],
        datasetTypeToStoreReadSettingControls: {
            [ModelConstants.binaryDataset]: getCommonBinaryStoreReadSettingControls(),
            [deltaMetadata.type]: getCommonDeltaStoreReadSettingControls()
        },
        datasetTypeToStoreWriteSettingControls: {
            [ModelConstants.binaryDataset]: [
                ...commonBinaryStoreWriteSettingControls,
                blockSizeInMbMetadata(blockSizeInMbDesc),
                {
                    type: 'store-setting-metadata',
                    bind: 'metadata',
                    options: [
                        {
                            label: '$$LASTMODIFIED',
                            value: '$$LASTMODIFIED',
                            tooltip: stringResources.Metadata_LastModified_Desc
                        }
                    ],
                    parameterizable: true
                }
            ],
            [ModelConstants.icebergDataset]: [
                maxConcurrentConnectionsMetadata,
                blockSizeInMbMetadata(blockSizeInMbDesc)
            ],
            [deltaMetadata.type]: [...commonDeltaStoreWriteSettingControls]
        },
        supportFirstClassParameterExperience: true,
        controls: [
            {
                type: 'select-switch',
                bind: 'authenticationMethod',
                label: stringResources.ModelName_AuthenticationType,
                options: mergeUserAuthCredentialOption(
                    mergeUamiCredentialOption(defaultAuthenticationOptions),
                    azureBlobFSAuthTypes.UserAuth
                ),
                isLabelVertical: true,
                defaultValue: 'AccountKeyAuthentication',
                cases: [
                    {
                        caseValue: azureBlobFSAuthTypes.AccountKeyAuthentication,
                        children: [azureBlobFSAccountSetting(true)]
                    },
                    {
                        caseValue: azureBlobFSAuthTypes.MSIAuthentication,
                        children: !GlobalVariables.isSamiSupportEnabled
                            ? [
                                azureBlobFSAccountSetting(false),
                                {
                                    type: 'management-identity-info',
                                    resourceType: stringResources.AzureBlobFS_DisplayText,
                                    link: 'https://learn.microsoft.com/azure/data-factory/connector-azure-data-lake-storage?tabs=data-factory#managed-identity'
                                }
                            ]
                            : [
                                azureBlobFSAuthReferenceSetting(CredentialTypeOptions.ManagedIdentity),
                                azureBlobFSAccountSetting(false)
                            ]
                    },
                    {
                        caseValue: azureBlobFSAuthTypes.ServicePrincipalAuthentication,
                        children: [
                            azureBlobFSAccountSetting(false),
                            azureBlobFSAuthReferenceSetting(CredentialTypeOptions.ServicePrincipal)
                        ]
                    },
                    {
                        caseValue: azureBlobFSAuthTypes.SasUriAuthentication,
                        children: [sasUriSetting(false, false, true, true)]
                    },
                    {
                        // for UAMI there won't be any radio buttons, since inline mode is not supported.
                        caseValue: azureBlobFSAuthTypes.UserAssignedManagedIdentity,
                        children: [
                            azureBlobFSAccountSetting(false),
                            {
                                type: 'credential-select',
                                bind: 'credential',
                                filterCredentialType: CredentialTypeOptions.UserAssignedManagedIdentity,
                                required: true,
                                validators: [{ type: 'required', errorMessage: '' }]
                            }
                        ]
                    },
                    {
                        caseValue: azureBlobFSAuthTypes.UserAuth,
                        children: [
                            azureBlobFSAccountSetting(false),
                            {
                                type: 'credential-select',
                                bind: 'credential',
                                hide: !GlobalVariables.enableConnectorUserAuthCred,
                                filterCredentialType: CredentialTypeOptions.UserAuth,
                                required: true,
                                validators: [{ type: 'required', errorMessage: '' }]
                            }
                        ]
                    }
                ]
            }
        ]
    };

    return metadata;
};
